import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, adminLogout, isAdmin, getAdminRole, getAdminUsername } from "@/lib/api";
import { Package, Utensils, CalendarCheck, Settings, Tag, FileBarChart, LogOut, Clock, Mail, User, Menu, X } from "lucide-react";
import OrdersPanel from "@/components/admin/OrdersPanel";
import ProductsPanel from "@/components/admin/ProductsPanel";
import ReservationsPanel from "@/components/admin/ReservationsPanel";
import SettingsPanel from "@/components/admin/SettingsPanel";
import HoursPanel from "@/components/admin/HoursPanel";
import PromosPanel from "@/components/admin/PromosPanel";
import AccountingPanel from "@/components/admin/AccountingPanel";
import EmailsPanel from "@/components/admin/EmailsPanel";

const ALL_TABS = [
  { id: "orders", label: "Commandes", icon: Package, roles: ["owner", "staff"] },
  { id: "reservations", label: "Réservations", icon: CalendarCheck, roles: ["owner", "staff"] },
  { id: "products", label: "Produits", icon: Utensils, roles: ["owner", "staff"] },
  { id: "promos", label: "Promos", icon: Tag, roles: ["owner", "staff"] },
  { id: "emails", label: "Emails clients", icon: Mail, roles: ["owner"] },
  { id: "hours", label: "Horaires", icon: Clock, roles: ["owner", "staff"] },
  { id: "accounting", label: "Comptabilité", icon: FileBarChart, roles: ["owner", "staff"] },
  { id: "settings", label: "Paramètres", icon: Settings, roles: ["owner"] },
];

export default function AdminDashboard() {
  const role = getAdminRole();
  const username = getAdminUsername();
  const TABS = ALL_TABS.filter(t => t.roles.includes(role));
  const [tab, setTab] = useState(TABS[0]?.id || "orders");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (!isAdmin()) { nav("/hana/dashboard"); return; }
    api.get("/admin/verify").catch(() => { adminLogout(); nav("/hana/dashboard"); });
  }, [nav]);

  const logout = () => { adminLogout(); nav("/hana/dashboard"); };
  const currentTab = TABS.find(t => t.id === tab);
  const CurrentIcon = currentTab?.icon;

  const selectTab = (id) => {
    setTab(id);
    setDrawerOpen(false);
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-background lg:flex">
      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-40 bg-primary text-white flex items-center justify-between px-4 h-14 shadow-sm">
        <button
          onClick={() => setDrawerOpen(true)}
          data-testid="admin-menu-btn"
          className="p-2 -ml-2 hover:bg-white/10 rounded-sm"
          aria-label="Menu"
        >
          <Menu size={22} />
        </button>
        <div className="flex items-center gap-2">
          {CurrentIcon && <CurrentIcon size={16} />}
          <span className="font-display text-lg">{currentTab?.label || "Admin"}</span>
        </div>
        <div className="text-[10px] uppercase tracking-wider bg-white/15 px-2 py-0.5 rounded-sm" data-testid="admin-role-mobile">{role === "owner" ? "owner" : "staff"}</div>
      </header>

      {/* Mobile drawer overlay */}
      {drawerOpen && (
        <div
          className="lg:hidden fixed inset-0 z-50 bg-black/60"
          onClick={() => setDrawerOpen(false)}
          data-testid="admin-drawer-overlay"
        />
      )}

      {/* Sidebar (desktop static, mobile drawer) */}
      <aside className={`
        bg-primary text-white flex flex-col
        fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] transition-transform duration-200
        ${drawerOpen ? "translate-x-0" : "-translate-x-full"}
        lg:static lg:translate-x-0 lg:w-64 lg:max-w-none
      `}>
        <div className="p-6 border-b border-white/10 flex items-start justify-between">
          <div>
            <div className="font-display text-2xl">Hanamiya</div>
            <div className="text-xs uppercase tracking-[0.2em] text-white/60 mt-1">Admin</div>
            <div className="mt-3 flex items-center gap-2 text-xs text-white/80 bg-white/10 px-2 py-1.5 rounded-sm">
              <User size={12} />
              <span data-testid="admin-username">{username}</span>
              <span className="ml-auto uppercase text-[10px] tracking-wider bg-white/15 px-1.5 rounded-sm" data-testid="admin-role">{role === "owner" ? "owner" : "staff"}</span>
            </div>
          </div>
          <button
            onClick={() => setDrawerOpen(false)}
            className="lg:hidden p-1 hover:bg-white/10 rounded-sm"
            aria-label="Fermer"
            data-testid="admin-drawer-close"
          >
            <X size={20} />
          </button>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                data-testid={`admin-tab-${t.id}`}
                onClick={() => selectTab(t.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm text-sm transition ${
                  tab === t.id ? "bg-white text-primary" : "text-white/85 hover:bg-white/10"
                }`}
              >
                <Icon size={18} />
                {t.label}
              </button>
            );
          })}
        </nav>
        <button onClick={logout} data-testid="admin-logout" className="m-3 px-4 py-3 rounded-sm text-sm flex items-center gap-3 text-white/70 hover:bg-white/10">
          <LogOut size={18} /> Déconnexion
        </button>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 overflow-x-hidden">
        <div className="p-4 sm:p-6 lg:p-8">
          {tab === "orders" && <OrdersPanel />}
          {tab === "reservations" && <ReservationsPanel />}
          {tab === "products" && <ProductsPanel />}
          {tab === "promos" && <PromosPanel />}
          {tab === "emails" && <EmailsPanel />}
          {tab === "hours" && <HoursPanel />}
          {tab === "accounting" && <AccountingPanel />}
          {tab === "settings" && <SettingsPanel />}
        </div>
      </main>
    </div>
  );
}
